import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CHAT_ANIMATIONS } from '@/utils/chat-animations';
import { Plus, Check, AlertCircle, Loader2, FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GlassPill from '@/components/ui/GlassPill';
import { ExtractedProject } from '@/hooks/ai-agents/functions/core/useRecommendationDetection';
import { useAgentChatProjectCreationWithTracking } from '@/hooks/ai-agents/functions/core/useAgentChatProjectCreationWithTracking';
import { useRecommendationCardState, RECOMMENDATION_CONFIGS } from '@/hooks/ai-agents/functions/core/createRecommendationTrackingWrapper';
import { useRecommendationTracking } from '@/hooks/ai-agents/functions/core/useRecommendationTracking';
import { useViewRecommendationNavigation } from '@/hooks/ai-agents/functions/core/useViewRecommendationNavigation';
import { useUser } from '@/hooks/auth/use-user';
import { useNavigate } from 'react-router-dom';
import { 
  getProjectNavigationUrl,
  useNavigationWithModalClose
} from '@/hooks/ai-agents/functions/core/useResponseParsing';
import { cn } from '@/lib/utils';
import { MessageDisplay } from '@/hooks/ai-agents/types/chat-hook-types';
import { 
  getCardContainerClasses, 
  getCardIconClasses, 
  getCardButtonClasses 
} from './shared/RecommendationCardStyles';

interface ChatRecommendedProjectCardProps {
  project: ExtractedProject;
  message: MessageDisplay;
  type?: 'project';
  className?: string;
}

type SaveState = 'idle' | 'saving' | 'success' | 'saved' | 'error';

/**
 * Chat Recommended Project Card - Individual project item with create button
 * Shows project recommendations with option to create new projects
 * Now with standardized recommendation tracking and consistent layout
 */
export const ChatRecommendedProjectCard: React.FC<ChatRecommendedProjectCardProps> = ({
  project,
  message,
  type = 'project',
  className
}) => {
  const { user } = useUser();
  const { navigateAndCloseModal } = useNavigationWithModalClose();
  
  // Use standardized tracking state management
  const { localState, setLocalState } = useRecommendationCardState(message, 'project');
  
  // Project creation with tracking
  const { mutateWithTracking, isGenerating } = useAgentChatProjectCreationWithTracking(message);
  
  // Get saved recommendation data for view functionality
  const { isRecommendationSaved } = useRecommendationTracking(message.id, message.databaseId);
  
  // Handle viewing the saved project
  const handleViewProject = async () => {
    const status = await isRecommendationSaved('project');
    if (status.isSaved && status.savedItemId && status.metadata) {
      const savedRecommendation = {
        id: status.savedItemId,
        type: 'project' as const,
        savedAt: status.savedAt!,
        metadata: status.metadata
      };
      
      // Navigate to goal page instead of project page
      const { goalId, projectId } = status.metadata;
      if (goalId) {
        console.log('[ChatRecommendedProjectCard] Navigating to goal:', goalId);
        // Use new route structure if project ID is available
        if (projectId) {
          navigateAndCloseModal(`/project/${projectId}/tasks`);
        } else {
          // Fallback to old route for backward compatibility during migration
          navigateAndCloseModal(`/task-manager/goal/${goalId}`);
        }
      } else {
        console.error('[ChatRecommendedProjectCard] No goalId found in metadata:', status.metadata);
      }
    } else {
      console.error('[ChatRecommendedProjectCard] No saved project data found');
    }
  };
  
  // Get config for this recommendation type
  const config = RECOMMENDATION_CONFIGS.project;

  // Handle saving this specific project
  const handleSaveProject = async () => {
    if (!user?.id) return;
    
    setLocalState('saving');
    
    try {
      await mutateWithTracking({
        userGoal: `${project.title}: ${project.description}`,
        title: project.title,
        description: project.description,
        type: project.type
      });
      
      setLocalState('saved');
      
    } catch (error) {
      console.error('[ChatRecommendedProjectCard] Failed to save project:', error);
      setLocalState('error');
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setLocalState('idle');
      }, 3000);
    }
  };

  // Get button content based on state
  const getButtonContent = () => {
    switch (localState) {
      case 'saving':
        return <Loader2 size={12} className="animate-spin" />;
      case 'success':
        return <Check size={12} />;
      case 'saved':
        return <FolderPlus size={12} />;
      case 'error':
        return <AlertCircle size={12} />;
      default:
        return <Plus size={12} />;
    }
  };

  // Get button text based on state
  const getButtonText = () => {
    switch (localState) {
      case 'saving':
        return config.savingText;
      case 'success':
        return config.successText;
      case 'saved':
        return config.savedText;
      case 'error':
        return config.errorText;
      default:
        return config.saveText;
    }
  };

  // Get button click handler based on state
  const getButtonHandler = () => {
    switch (localState) {
      case 'saved':
        return handleViewProject;
      default:
        return handleSaveProject;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        variants={CHAT_ANIMATIONS.cardEntry}
        initial="initial"
        animate="animate"
        exit="exit"
        className={cn("mt-3 w-full", className)}
      >
        <div className={cn(getCardContainerClasses(type))}>
          <div className="flex items-start gap-2.5">
            {/* Create Button - Left Side */}
            <div className="flex-shrink-0 pt-0.5">
              <Button
                size="sm"
                variant="outline"
                onClick={getButtonHandler()}
                disabled={localState === 'saving' || localState === 'success'}
                className={cn(
                  "h-7 px-3 text-xs border-rose-300 dark:border-rose-600",
                  "hover:bg-rose-50 dark:hover:bg-rose-900/20",
                  "transition-all duration-200",
                  // State-specific styling
                  localState === 'success' && 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
                  localState === 'saved' && 'border-gray-300 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200',
                  localState === 'error' && 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
                )}
              >
                <span className="flex items-center gap-1.5">
                  {getButtonContent()}
                  <span>{getButtonText()}</span>
                </span>
              </Button>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <span className="text-sm text-gray-700 dark:text-gray-200 block font-medium">
                {project.title}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                {project.description}
              </p>
              
              {/* Project Type Pill */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <GlassPill 
                  type={project.type} 
                  variant="rose"
                />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}; 